/**
 * LA FILE gtag PARTAGÉE — GTM, GA4 et Google Ads lisent tous la même.
 *
 * `window.gtag` est défini par le bloc Consent Mode de `index.html`, AVANT
 * le conteneur GTM : c'est la porte d'entrée unique vers `dataLayer` pour
 * les commandes gtag (`js`, `config`, `event`, `consent`). On ne le
 * redéfinit PAS s'il est là — écraser la file en cours de consommation serait
 * destructif. On ne le crée que s'il manque : bloqueur de contenu qui a coupé
 * le snippet du head, ou environnement sans page (le prérendu tourne sous
 * Node, sans `window`).
 *
 * La forme poussée est celle du snippet officiel — l'objet `arguments` —
 * parce que c'est ce que gtag.js relit dans la file. Ce module ne contient
 * AUCUN identifiant de mesure et ne charge aucun script tiers : il donne
 * seulement accès à la file (même frontière que `analytics.ts`).
 */

interface GtagWindow extends Window {
  gtag?: (...args: unknown[]) => void;
  dataLayer?: unknown[];
}

/** La fonction gtag, ou `null` hors navigateur. */
export function gtagQueue(): ((...args: unknown[]) => void) | null {
  if (typeof window === 'undefined') return null;
  const w = window as GtagWindow;
  if (typeof w.gtag === 'function') return w.gtag;
  w.dataLayer = w.dataLayer || [];
  // eslint-disable-next-line func-style -- la forme « function » seule donne un vrai `arguments`.
  const fn = function () {
    // La file se recrée si elle a été retirée en cours de session : la mesure
    // ne casse jamais une page, pas même celle d'un test qui supprime
    // `dataLayer` — c'est aussi ce que fait le snippet officiel de index.html
    // (`window.dataLayer = window.dataLayer || []`).
    w.dataLayer = w.dataLayer || [];
    // eslint-disable-next-line prefer-rest-params -- `arguments` est la charge utile que gtag.js attend.
    (w.dataLayer as unknown[]).push(arguments);
  };
  w.gtag = fn;
  return fn;
}
